import React, { useEffect, useRef, useState } from "react";
import { Typography, Box, useMediaQuery, useTheme, Button } from "@mui/material";
import { Link, NavLink } from "react-router-dom";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import MovieCard from "./MovieCard";
import useDragScroll from "../hooks/useDragScroll";
import "../App.css";

const SCROLL_STEP = 400;

const MovieRow = ({ title, seeAllPath, fetcher }) => {
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const theme = useTheme();
  const showArrows = useMediaQuery(theme.breakpoints.up('sm'));

  useDragScroll(scrollRef);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    fetcher(1, { signal: controller.signal })
      .then((response) => {
        setResults(response.data?.results || []);
      })
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setError("Could not load movies. Please try again.");
      });
    return () => controller.abort();
  }, [fetcher]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollLeft += direction === 'left' ? -SCROLL_STEP : SCROLL_STEP;
  };

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" component="h2" sx={{ color: '#fff', mb: 2 }} role="alert">
          {error}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            setError(null);
            setResults([]);
            const controller = new AbortController();
            fetcher(1, { signal: controller.signal })
              .then((response) => setResults(response.data?.results || []))
              .catch((err) => {
                if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
                setError("Could not load movies. Please try again.");
              });
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <section aria-label={title}>
      <div className="seeall">
        <Typography variant="h6" component="h2" className="title">
          {title}
        </Typography>
        {seeAllPath && (
          <NavLink to={seeAllPath} style={{ textDecoration: 'none', color: '#B3B3B3' }}>
            See All
          </NavLink>
        )}
      </div>
      <div className="avatarS">
        {showArrows && (
          <button
            type="button"
            className="left"
            onClick={() => scroll('left')}
            aria-label={`Scroll ${title} left`}
          >
            <ArrowLeftIcon sx={{ color: 'white', fontSize: 32 }} />
          </button>
        )}
        <div className="scroll" ref={scrollRef}>
          {results.map((movie) => (
            <Link
              key={movie.id}
              to={`/movie/${movie.id}`}
              aria-label={`View details for ${movie.title || movie.original_name}`}
            >
              <div className="cardMovie">
                <MovieCard data={movie} />
              </div>
            </Link>
          ))}
        </div>
        {showArrows && (
          <button
            type="button"
            className="left"
            onClick={() => scroll('right')}
            aria-label={`Scroll ${title} right`}
          >
            <ArrowRightIcon sx={{ color: 'white', fontSize: 32 }} />
          </button>
        )}
      </div>
    </section>
  );
};

export default MovieRow;
